import { Component, Injector, ViewEncapsulation } from '@angular/core';
import {
    ClassNames,
    DateFormat,
    getDynamicRouteLink,
    Styling,
    Utilities
} from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById, ResponsiveService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { createNoHistoricalIneligiblesDataMessage } from '../../../constants/copy.constants';
import {
    GetIneligiblesRequest,
    GetIneligiblesResponse,
    IneligibleInvoice
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    atlas,
    CUSTOMER_INELIGIBLES,
    INVOICES_INVOICE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GetIneligiblesService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateAsOfReportComponent } from '../base/date-as-of-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';

@Component({
    selector: 'ineligibles',
    template: `
        <div
            [ngClass]="{ 'min-page-width-medium-up': !(dataSource?.loading$ | async) }"
            class="ineligibles-report-page"
        >
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="apiService"
                [hideExport]="dataSource.noData$ | async"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>
            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [noDataMessage]="getCustomMessage()"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="mtc-header-content">
                                    <div class="mtc-header-title">
                                        {{ row.invoiceNumber }}
                                    </div>
                                    <div class="mtc-header-sub-title">
                                        {{
                                            ineligibleAmountHeader1 + ' ' + ineligibleAmountHeader2
                                        }}: {{ row.ineligibleAmount | lsqCurrency }}
                                    </div>
                                </div>
                            </ng-template>
                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ submissionDateHeader1 + ' ' + submissionDateHeader2 }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.submissionDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ debtorNameHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            [routerLink]="[
                                                getCustomerLink(
                                                    ineligiblesByCustomerRouterLinkUrl,
                                                    row.debtorKey
                                                )
                                            ]"
                                            class="preserve-case"
                                            [queryParams]="{
                                                date: utilities.getISOStringWithoutTimezone(
                                                    selectedDate
                                                )
                                            }"
                                        >
                                            {{ row.debtorName }}
                                        </a>
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ invoiceNumberHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            [routerLink]="[
                                                getInvoiceLink(
                                                    invoiceDetailRouterLinkUrl,
                                                    row.invoiceKey
                                                )
                                            ]"
                                            class="preserve-case"
                                        >
                                            {{ row.invoiceNumber }}
                                        </a>
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ invoicDateHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.invoiceDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ ineligibleDateHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.ineligibleDate | date: dateFormat.LsqShortYear }}
                                    </div>
                                </div>
                                <div class="card-item">
                                    <div class="card-item-label">
                                        {{ reasonHeader }}
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.ineligibleReason }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            [style.display]="(responsiveService.smBelow | async) ? 'none' : 'block'"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasFooter]="true"
                            [hasStickyHeader]="true"
                            [noDataMessage]="getCustomMessage()"
                            (takeChanged)="handleTakeChange($event)"
                        >
                            <lsq-row-count-column></lsq-row-count-column>
                            <lsq-date-column
                                [column]="{
                                    header:
                                        submissionDateHeader1 + '<br />' + submissionDateHeader2,
                                    key: 'submissionDate'
                                }"
                            >
                                <ng-template [custom-footer-cell] let-row="row">
                                    <div class="grand-total">
                                        <span>Grand Total</span>
                                    </div>
                                </ng-template>
                            </lsq-date-column>
                            <lsq-column
                                [column]="{
                                    header: debtorNameHeader,
                                    key: 'debtorName',
                                    truncateTextCss: true,
                                    className: 'customer-name'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        [routerLink]="[
                                            getCustomerLink(
                                                ineligiblesByCustomerRouterLinkUrl,
                                                row.debtorKey
                                            )
                                        ]"
                                        [queryParams]="{
                                            date: utilities.getISOStringWithoutTimezone(
                                                selectedDate
                                            )
                                        }"
                                        class="preserve-case"
                                    >
                                        {{ row.debtorName }}
                                    </a>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: invoiceNumberHeader,
                                    key: 'invoiceNumber',
                                    truncateTextCss: true,
                                    className: 'invoice-number'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        [routerLink]="[
                                            getInvoiceLink(
                                                invoiceDetailRouterLinkUrl,
                                                row.invoiceKey
                                            )
                                        ]"
                                        class="preserve-case"
                                    >
                                        {{ row.invoiceNumber }}
                                    </a>
                                </ng-template>
                            </lsq-column>
                            <lsq-date-column
                                [column]="{
                                    header: invoicDateHeader,
                                    key: 'invoiceDate'
                                }"
                            >
                            </lsq-date-column>
                            <lsq-date-column
                                [column]="{
                                    header: ineligibleDateHeader,
                                    key: 'ineligibleDate'
                                }"
                            >
                            </lsq-date-column>
                            <lsq-currency-column
                                [column]="{
                                    header:
                                        ineligibleAmountHeader1 +
                                        '<br />' +
                                        ineligibleAmountHeader2,
                                    key: 'ineligibleAmount',
                                    width: styling.TableColumn_TightWidth
                                }"
                                [totalValue]="(dataSource.data$ | async)?.totalIneligibleAmount"
                            >
                            </lsq-currency-column>
                            <lsq-column
                                [column]="{
                                    header: reasonHeader,
                                    key: 'ineligibleReason',
                                    truncateTextCss: true
                                }"
                            >
                            </lsq-column>
                        </lsq-data-table>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./ineligibles.report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class IneligiblesReportComponent extends DateAsOfReportComponent {
    public title = this._i18n({
        value: 'Ineligibles',
        meaning: 'Page title'
    });
    public analyticsName = AnalyticsValue.Ineligibles;
    public selectedDate: Date;

    public userStateType = ReportsUserStateTypes.GetIneligiblesRequest;
    public savedDateFilterName = null;

    // Constants.
    public classNames = ClassNames;
    public dateFormat = DateFormat;
    public styling = Styling;
    public utilities = Utilities;
    public ineligiblesByCustomerRouterLinkUrl = getRouterLinkUrlById(atlas, CUSTOMER_INELIGIBLES);
    public invoiceDetailRouterLinkUrl = getRouterLinkUrlById(atlas, INVOICES_INVOICE);

    public dataSource = new LsqApiServiceListSource<
        GetIneligiblesResponse,
        GetIneligiblesRequest,
        IneligibleInvoice
    >(this.apiService);

    public submissionDateHeader1 = this._i18n({
        value: 'Submission',
        meaning: 'Table column header - Part 1/2 of: Submission Date'
    });
    public submissionDateHeader2 = this._i18n({
        value: 'Date',
        meaning: 'Table column header - Part 2/2 of: Submission Date'
    });
    public dateMobileHeader = this._i18n({
        value: 'Submission Date',
        meaning: 'Table column header'
    });
    public debtorNameHeader = this._i18n({
        value: 'Customer',
        meaning: 'Table column header'
    });
    public invoiceNumberHeader = this._i18n({
        value: 'Invoice #',
        meaning: 'Table column header'
    });
    public invoicDateHeader = this._i18n({
        value: 'Invoice Date',
        meaning: 'Table column header'
    });
    public ineligibleDateHeader = this._i18n({
        value: 'Ineligible Date',
        meaning: 'Table column header'
    });
    public ineligibleAmountHeader1 = this._i18n({
        value: 'Ineligible',
        meaning: 'Table column header - Part 1/2 of: Ineligible Amount'
    });
    public ineligibleAmountHeader2 = this._i18n({
        value: 'Amount',
        meaning: 'Table column header - Part 2/2 of: Ineligible Amount'
    });
    public reasonHeader = this._i18n({
        value: 'Reason',
        meaning: 'Table column header'
    });

    constructor(
        private _i18n: I18n,
        private _appSettings: AppSettings,
        public apiService: GetIneligiblesService,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();
    }

    public getInvoiceLink(link: string, invoiceKey: string): string {
        return getDynamicRouteLink(link, { invoiceKey });
    }

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }

    public getCustomMessage(): string {
        if (
            this.selectedDate &&
            this.selectedDate < this._appSettings.historicalIneligiblesStartDate
        ) {
            return createNoHistoricalIneligiblesDataMessage(
                this._appSettings.historicalIneligiblesStartDate
            );
        }
        return undefined;
    }
}
